import { useQuery } from '@change-corgi/core-react/async';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	PetitionStats,
	PetitionStatsErrorReason,
	PetitionStatsState,
} from '#src/app/pages/petitionDetails/shared/types';

import { getPetitionStats } from './api/stats';

export type { PetitionStatsState };

async function getPetitionStatsWithNormalizedError(
	slug: string,
	utilityContext: UtilityContext,
): Promise<PetitionStats | undefined> {
	try {
		const stats = await getPetitionStats(slug, utilityContext);
		return stats;
	} catch (e) {
		throw new Error('other' satisfies PetitionStatsErrorReason, { cause: e });
	}
}

async function getPetitionStatsOrThrow(slug: string, utilityContext: UtilityContext): Promise<PetitionStats> {
	const stats = await getPetitionStatsWithNormalizedError(slug, utilityContext);
	if (!stats) {
		throw new Error('notFound' satisfies PetitionStatsErrorReason);
	}
	return stats;
}

export function useApiPetitionStats(slug: string): PetitionStatsState {
	const utilityContext = useUtilityContext();

	return useQuery(
		async () => ({ stats: await getPetitionStatsOrThrow(slug, utilityContext) }),
		[slug, utilityContext],
		{
			loadingBetweenQueries: true, // necessary for recent signers to properly update between pages
			errorHandler: (e: Error) => ({ reason: e.message as PetitionStatsErrorReason }),
		},
	);
}
